/**
 * Interceptor HTTP funcional que adjunta `Authorization: Bearer <token>` cuando corresponde y
 * ejecuta {@link AuthService.logout} ante **401/403**, salvo peticiones a `cache/clear`
 * (evita doble cierre de sesión si el token ya fue renovado).
 *
 * @remarks Registrado globalmente en {@link appConfig}; forma parte del módulo conceptual **Auth**
 * (documentación de módulo en `auth.service.ts`).
 * @see AuthService
 */

import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { VerifyCodeService } from './verify-code.service';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { AuthStatus } from '../../../core/models/auth.model';

/** Interceptor registrado en `provideHttpClient(withInterceptors([...]))`. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const verifyCodeService = inject(VerifyCodeService);
  const authService = inject(AuthService);

  const token = authService.accessToken();
  if (!token) return next(req);

  const isCacheClearRequest = req.url.includes('/cache/clear');
  const shouldAddToken =
    isCacheClearRequest ||
    (verifyCodeService.isFirstlog() ||
      authService.authStatus() === AuthStatus.authenticated);

  if (!shouldAddToken) return next(req);

  const reqWithAccessToken = req.clone({
    headers: req.headers.append('Authorization', `Bearer ${token}`),
  });

  return next(reqWithAccessToken).pipe(
    catchError((error: HttpErrorResponse) => {
      // `cache/clear` lo maneja `AuthService.logout()`; un 401 aquí no debe forzar otro logout
      // (p. ej. token ya reemplazado por un login nuevo mientras la petición anterior volvía).
      if (
        !isCacheClearRequest &&
        (error.status === HttpStatusCode.Unauthorized ||
          error.status === HttpStatusCode.Forbidden)
      ) {
        authService.logout();
      }
      return throwError(() => error);
    }),
  );
};
